import {
    BRAND_NAME,
    BRAND_THEME,
    GA4_DEBUG_MODE,
    GA4_ENABLED,
    GA4_ID,
    GA4_ROLLUP_ENABLED,
    GA4_ROLLUP_ID,
    GA4_TRAFFIC_TYPE_INTERNAL,
    IS_LOGGED_IN,
    IS_STAGING,
    PLAYER_ID
} from "Setup/config";
import {gtag} from 'ga-gtag';
import {sendLogglyEvent} from "Helpers/loggly";

/**
 * Where every event goes: the brand's own property, and the cumulative property
 * that all the brands share. send_to takes a list of measurement IDs, so both
 * are served by the one call - the brand properties carry on exactly as before.
 *
 * A brand with no measurement ID of its own (nothing for it in the dictionary
 * in Helpers/index) still reports into the cumulative one.
 *
 * Resolved on each call rather than once at module scope, deliberately: this
 * module sits in an import cycle (Setup/config imports Helpers/index for
 * getMeasurementId, and Helpers/index imports this file), so reading a config
 * binding while the modules are still initialising throws on GA4_ID. Everything
 * here must touch Setup/config from inside a function body.
 */
const getGA4Targets = () => [GA4_ID, GA4_ROLLUP_ENABLED && GA4_ROLLUP_ID].filter(Boolean);

/**
 * Sends the event to GA4's DebugView, which ignores anything without this flag.
 * Off in production - see GA4_DEBUG_MODE. Spread before the caller's parameters
 * so a call site can still turn it off for a particular event.
 */
const getDebugModeParameter = () => (GA4_DEBUG_MODE ? {debug_mode: true} : {});

/**
 * Marks the event as ours rather than a real player's, so GA4's Internal
 * Traffic data filter drops it and the reports hold production traffic only.
 * On for QA3 and staging - see GA4_TRAFFIC_TYPE_INTERNAL. Spread before the
 * caller's parameters, as above, so a call site can still override it.
 *
 * The filter has to exist on the property for this to do anything: Admin ->
 * Data filters -> Internal Traffic, set to Active (not Testing, which only
 * tags the traffic), on each brand property and on the roll-up.
 */
const getTrafficTypeParameter = () => (GA4_TRAFFIC_TYPE_INTERNAL ? {traffic_type: 'internal'} : {});

/**
 * gtag.js is loaded and configured by the host page, for the brand's own
 * property only. GA4 drops an event sent to a measurement ID that nothing on
 * the page has configured, so the cumulative property has to be configured here
 * before any event can reach it.
 *
 * Configuring it also brings in the automatically collected events - page_view,
 * session_start, first_visit, scroll - which is most of what makes the property
 * worth having: it sees the same traffic the brand properties do, rather than
 * only the events Morpheus sends by hand.
 *
 * Consent Mode is set globally by the host page ahead of its own tag, so this
 * property inherits the same consent state and there is nothing to repeat here.
 */
export const initialiseGA4RollupTracking = () => {
    try {
        if (!GA4_ENABLED || !GA4_ROLLUP_ENABLED || !GA4_ROLLUP_ID) return;

        window.dataLayer = window.dataLayer || [];

        // Commands queue in dataLayer and are read when gtag.js loads, so the
        // order against the host page's own tag does not matter. But if there
        // is no gtag.js on the page at all - a brand running GTM alone, say -
        // nothing would ever read them, so load a copy.
        if (!document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) {
            const script = document.createElement("script");
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ROLLUP_ID}`;
            document.head.appendChild(script);

            gtag("js", new Date());
        }

        // Config parameters are applied to every event sent to this measurement
        // ID, including the automatic ones, so the cumulative numbers can always
        // be split back out by brand. The brand's own property never sees them.
        // `page_type` separates this traffic from the landing pages, which report
        // into the same property from their own service (LandingPages, in
        // resources/views/partials/gtm.blade.php) and send `landing_page`.
        // Without it here, every Morpheus session reports as "(not set)".
        gtag("config", GA4_ROLLUP_ID, {
            brand: BRAND_NAME,
            brand_theme: BRAND_THEME,
            page_type: 'site',
            ...getDebugModeParameter(),
            ...getTrafficTypeParameter()
        });
    } catch (e) {
        sendLogglyEvent('ga4RollupInitError', e.message);
    }
};

export const sendLaunchFirstDepositEvent = (bonusType, promoCode) => {
    sendGA4Event(`banking_launch`, {
        bonus_type: bonusType,
        promo_code: promoCode,
    });
};

export const sendGAEvents = ({category = "", action = "", label = ""}) => {
    if (!category.length || !action.length) return;

    let ga4Obj = {action};
    if (label.length) ga4Obj.label = label;

    sendGA4Event(category, ga4Obj);
};

/**
 * GA4's reserved User-ID field, so behaviour recorded here can be joined to the
 * player's activity in our own data - without it GA4 can show what happened but
 * not who it happened to beyond a per-device pseudonym.
 *
 * PLAYER_ID is our internal, pseudonymous identifier. Never send an email,
 * username or anything else that identifies a person: Google's terms prohibit
 * it, and it cannot be removed once collected.
 *
 * The same value goes to both properties, so the cumulative property counts a
 * player once across the brands they play on rather than once per brand.
 *
 * This covers the events Morpheus sends. Automatically collected events
 * (page_view, session_start and so on) are fired by the host page's tag, so
 * carrying the user id on those needs the equivalent change there.
 */
const getUserIdParameter = () => (IS_LOGGED_IN && PLAYER_ID ? {user_id: String(PLAYER_ID)} : {});

export const sendGA4Event = (name, parameters) => {
    try {
        if (IS_STAGING) {
            console.log(name, JSON.stringify(parameters));
        }

        const sendTo = getGA4Targets();

        if (GA4_ENABLED && sendTo.length) {
            gtag('event', name, {
                send_to: sendTo,
                ...getUserIdParameter(),
                ...getDebugModeParameter(),
                ...getTrafficTypeParameter(),
                ...parameters
            });
        }
    }catch(e){
        sendLogglyEvent('ga4eventError', e.message);
    }
};
